import { BoothOtpMode, CibliError, OTP_VALIDITY_SECONDS } from '@cibli/shared';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import type { UserInfo } from '@/features/register/context/register.context';

import { OTPRateLimitError, useOTPSender } from '@/features/auth/otp';
import { normalizePhoneNumber } from '@/lib/utils/phone';
import { createKioskSession } from '@/services/kiosk-auth';
import { clearOtpCache } from '@/services/otp';
import { checkUserExists, createUser } from '@/services/user';

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

/** Kiosk OTP action cooldowns, per booth OTP mode. */
export const EMAIL_RESEND_COOLDOWN_SECONDS = 5;
export const SMS_FALLBACK_DELAY_SECONDS = 45;
export const SMS_RESEND_COOLDOWN_SECONDS = 30;
export const EMAIL_FALLBACK_DELAY_SECONDS = 30;

type UseKioskOtpParams = {
  login: (userInfos?: UserInfo) => Promise<boolean>;
  onSkipOtpSuccess?: (userId: string) => Promise<void>;
  isDevOtpBypassEnabled?: boolean;
  /** Booth OTP mode. Defaults to SMS (historical behaviour). */
  otpMode?: BoothOtpMode;
  /** Machine UID of the booth — required to create the session in DISABLED mode. */
  machineUid?: string;
};

type OtpStateBase = {
  phoneNumber: string;
  displayPhone: string;
  hasExistingOtp: boolean;
  timeLeft?: number;
  userEmail?: string;
};

export type OtpState =
  | ({ mode: 'sms'; smsError?: boolean } & OtpStateBase)
  | ({ mode: 'email'; emailFallback: string } & OtpStateBase);

type UseKioskOtpReturn = {
  otpState: OtpState | null;
  isSendingOtp: boolean;
  startOtpFlow: (userInfos: UserInfo) => Promise<void>;
  handleOtpVerificationSuccess: () => Promise<void>;
  handleOtpResend: () => Promise<void>;
  handleEmailFallback: (email: string) => Promise<void>;
  handleSmsFallback: (phone?: string) => Promise<void>;
  handleClearSmsError: () => void;
  handleOtpBack: () => void;
  clearOtpState: () => void;
};

async function retryCheckUserExists(
  phone: string,
  isEmail: boolean,
  retries = MAX_RETRIES,
): Promise<{ exists: boolean }> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await checkUserExists(phone, isEmail, { showErrorToast: false });
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }
  }
  throw lastError;
}

function getNetworkErrorMessage(error: unknown): string {
  // Check for network errors (no response)
  if (error instanceof Error && error.message === 'Network Error') {
    return 'Erreur de connexion. Vérifiez votre connexion internet et réessayez.';
  }

  // Check for CibliError with no response
  if (error instanceof CibliError && !error.response) {
    return 'Erreur de connexion. Vérifiez votre connexion internet et réessayez.';
  }

  // Check for CibliError with error code
  if (error instanceof CibliError && error.response?.data?.code) {
    const code = error.response.data.code;
    if (code === 'SERVER_ERROR' || code === 'INTERNAL_ERROR') {
      return 'Le serveur est temporairement indisponible. Réessayez dans quelques instants.';
    }
  }

  return 'Une erreur est survenue. Veuillez réessayer.';
}

function isSmsDeliveryFailed(error: unknown): boolean {
  if (error instanceof CibliError) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (!data) return false;
    // Check top-level code (if exception filter flattens) or nested in message (NestJS default)
    if (data.code === 'SMS_DELIVERY_FAILED') return true;
    const msg = data.message;
    if (typeof msg === 'object' && msg !== null && 'code' in msg) {
      return (msg as Record<string, unknown>).code === 'SMS_DELIVERY_FAILED';
    }
    return false;
  }
  return false;
}

function isArchivedError(error: unknown): boolean {
  if (error instanceof CibliError) {
    return error.response?.data?.code === 'account_archived';
  }
  return false;
}

export const useKioskOtp = ({
  login,
  onSkipOtpSuccess,
  isDevOtpBypassEnabled = false,
  otpMode = BoothOtpMode.SMS,
  machineUid,
}: UseKioskOtpParams): UseKioskOtpReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sendOTP } = useOTPSender();
  const [otpState, setOtpState] = useState<OtpState | null>(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  // Last submitted registration data, kept to save a phone given after a skipped step
  const lastUserInfosRef = useRef<UserInfo | null>(null);

  const startOtpFlow = useCallback(
    async (userInfos: UserInfo): Promise<void> => {
      if (isSendingOtp) return;
      setIsSendingOtp(true);
      const normalizedPhone = normalizePhoneNumber(userInfos.phone);
      lastUserInfosRef.current = userInfos;

      try {
        // DISABLED: no OTP at all — create/refresh the account and continue the journey
        if (otpMode === BoothOtpMode.DISABLED) {
          await login(userInfos);
          // OTP is skipped: no better-auth session cookie is set by the flow.
          // Create one explicitly so authenticated endpoints (jobs,
          // applications, dashboard) accept the kiosk requests. Throwing on a
          // missing machineUid surfaces the problem instead of silently
          // reproducing the 401 empty-jobs bug.
          if (!machineUid) {
            throw new Error('Booth machineUid missing: cannot create kiosk session');
          }
          await createKioskSession(machineUid, normalizedPhone);
          navigate('/payment', { replace: true });
          return;
        }

        const isEmailMode = otpMode === BoothOtpMode.EMAIL;
        // EMAIL mode identifies the candidate by email (the phone may have been skipped)
        const identifier = isEmailMode ? (userInfos.email ?? '') : normalizedPhone;

        if (isEmailMode && !identifier) {
          toast.error(t('formErrors.required'));
          return;
        }

        // Check if the identifier already exists in DB (with retry)
        const { exists } = await retryCheckUserExists(identifier, isEmailMode);

        if (!exists) {
          // First time — create user, then require OTP verification
          await createUser(userInfos, { showErrorToast: false });
        }

        // OTP flow for all users (new and returning)
        // Clear any previous OTP for this identifier before starting a new kiosk session
        await clearOtpCache(identifier, isEmailMode);

        // Upsert user data before OTP (existing account update or creation)
        await createUser(userInfos, { showErrorToast: false });

        if (isEmailMode) {
          // Email mode: the code goes to the email filled two steps earlier
          const result = await sendOTP({
            identifier,
            isEmail: true,
            checkExisting: true,
            enforceRateLimit: false, // Kiosk doesn't enforce rate limit on initial send
          });

          setOtpState({
            mode: 'email',
            emailFallback: identifier,
            phoneNumber: normalizedPhone,
            displayPhone: userInfos.phone,
            hasExistingOtp: result.hasExistingOtp,
            timeLeft: result.timeLeft,
            userEmail: userInfos.email,
          });
          navigate('/otp', { replace: true });
          return;
        }

        // Dev bypass: skip SMS sending, go directly to OTP screen (only for dev phone number)
        const devPhone = import.meta.env.VITE_DEV_USER_PHONE;
        if (isDevOtpBypassEnabled && devPhone && normalizedPhone === devPhone) {
          // Dev bypass: send OTP so verification record exists, then show OTP screen
          // The backend sendSMSOTP callback will override the code to dev code
          await sendOTP({
            identifier: normalizedPhone,
            isEmail: false,
          });
          setOtpState({
            mode: 'sms',
            phoneNumber: normalizedPhone,
            displayPhone: userInfos.phone,
            hasExistingOtp: false,
            timeLeft: OTP_VALIDITY_SECONDS,
            userEmail: userInfos.email,
          });
          navigate('/otp', { replace: true });
          return;
        }

        // Send OTP (will check for existing OTP and handle rate limiting)
        const result = await sendOTP({
          identifier: normalizedPhone,
          isEmail: false,
          checkExisting: true,
          enforceRateLimit: false, // Kiosk doesn't enforce rate limit on initial send
        });

        setOtpState({
          mode: 'sms',
          phoneNumber: normalizedPhone,
          displayPhone: userInfos.phone,
          hasExistingOtp: result.hasExistingOtp,
          timeLeft: result.timeLeft,
          userEmail: userInfos.email,
        });
        navigate('/otp', { replace: true });
      } catch (error) {
        // Check if it's an archived account error - let parent handle display
        if (isArchivedError(error)) {
          throw error;
        }

        // Check if SMS delivery failed - show modal for email fallback
        if (isSmsDeliveryFailed(error)) {
          setOtpState({
            mode: 'sms',
            smsError: true,
            phoneNumber: normalizedPhone,
            displayPhone: userInfos.phone,
            hasExistingOtp: false,
            timeLeft: OTP_VALIDITY_SECONDS,
            userEmail: userInfos.email,
          });
          navigate('/otp', { replace: true });
          return;
        }

        // Show user-friendly error message for network/other errors
        // Don't throw - toast is sufficient feedback for kiosk users
        const errorMessage = getNetworkErrorMessage(error);
        toast.error(errorMessage);
      } finally {
        setIsSendingOtp(false);
      }
    },
    [
      isSendingOtp,
      isDevOtpBypassEnabled,
      login,
      machineUid,
      navigate,
      onSkipOtpSuccess,
      sendOTP,
      otpMode,
      t,
    ],
  );

  const handleOtpVerificationSuccess = useCallback(async () => {
    const success = await login();

    if (success) {
      navigate('/payment', { replace: true });
      setOtpState(null);
    } else {
      toast.error(t('KioskOTP.verifyError'));
    }
  }, [login, navigate, t]);

  const handleOtpResend = useCallback(async () => {
    if (!otpState) return;

    try {
      if (isSendingOtp) return;
      setIsSendingOtp(true);

      // Determine if we should resend via email or SMS
      const isEmail = otpState.mode === 'email';
      const identifier = isEmail ? otpState.emailFallback : otpState.phoneNumber;

      // Send new OTP (enforces rate limiting)
      const result = await sendOTP({
        identifier,
        isEmail,
        checkExisting: true,
        enforceRateLimit: true,
        rateLimitSeconds: isEmail ? EMAIL_RESEND_COOLDOWN_SECONDS : SMS_RESEND_COOLDOWN_SECONDS,
      });

      setOtpState(prev => {
        if (!prev) return prev;
        if (prev.mode === 'email') {
          return {
            ...prev,
            hasExistingOtp: result.hasExistingOtp,
            timeLeft: result.timeLeft,
          };
        }
        return {
          ...prev,
          hasExistingOtp: result.hasExistingOtp,
          timeLeft: result.timeLeft,
        };
      });
    } catch (error) {
      if (error instanceof OTPRateLimitError) {
        toast.error(t('dashboard.auth.otp.rateLimitError', { seconds: error.waitTimeSeconds }), {
          duration: 4000,
        });
      } else {
        toast.error(getNetworkErrorMessage(error) || t('formErrors.resendFailed'));
      }
    } finally {
      setIsSendingOtp(false);
    }
  }, [isSendingOtp, otpState, sendOTP, t]);

  const handleEmailFallback = useCallback(
    async (email: string): Promise<void> => {
      if (!otpState) return;

      try {
        // Send OTP via email
        const result = await sendOTP({
          identifier: email,
          isEmail: true,
          checkExisting: false,
          enforceRateLimit: true, // Enforce rate limiting for email fallback too
        });

        // Update state to use email fallback
        setOtpState(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            mode: 'email',
            emailFallback: email,
            hasExistingOtp: result.hasExistingOtp,
            timeLeft: result.timeLeft,
          };
        });

        toast.success(t('KioskOTP.emailOtpSent'));
      } catch {
        toast.error(t('KioskOTP.emailOtpFailed'));
        // Don't throw - toast is sufficient feedback
      }
    },
    [otpState, sendOTP, t],
  );

  /** EMAIL mode fallback: send the code by SMS instead (invalidates the email code). */
  const handleSmsFallback = useCallback(
    async (phone?: string): Promise<void> => {
      if (!otpState || otpState.mode !== 'email') return;

      const targetPhone = normalizePhoneNumber(phone || otpState.phoneNumber);
      if (!targetPhone) return;

      try {
        setIsSendingOtp(true);

        // Save the phone if it was skipped (or changed) at the phone step
        const lastUserInfos = lastUserInfosRef.current;
        if (lastUserInfos && normalizePhoneNumber(lastUserInfos.phone) !== targetPhone) {
          await createUser({ ...lastUserInfos, phone: targetPhone }, { showErrorToast: false });
        }

        // Sending by SMS cancels the code sent by email
        await clearOtpCache(otpState.emailFallback, true);

        const result = await sendOTP({
          identifier: targetPhone,
          isEmail: false,
          checkExisting: true,
          enforceRateLimit: true,
          rateLimitSeconds: SMS_RESEND_COOLDOWN_SECONDS,
        });

        setOtpState(prev => {
          if (!prev || prev.mode !== 'email') return prev;
          return {
            ...prev,
            mode: 'sms',
            phoneNumber: targetPhone,
            displayPhone: phone ?? prev.displayPhone,
            hasExistingOtp: result.hasExistingOtp,
            timeLeft: result.timeLeft,
          };
        });

        toast.success(t('KioskOTP.smsOtpSent'));
      } catch (error) {
        if (error instanceof OTPRateLimitError) {
          toast.error(t('dashboard.auth.otp.rateLimitError', { seconds: error.waitTimeSeconds }), {
            duration: 4000,
          });
        } else if (isSmsDeliveryFailed(error)) {
          // Flag the SMS failure so the screen offers the email fallback again
          setOtpState(prev =>
            prev && prev.mode === 'email'
              ? { ...prev, mode: 'sms', smsError: true, phoneNumber: targetPhone }
              : prev,
          );
        } else {
          toast.error(getNetworkErrorMessage(error) || t('formErrors.resendFailed'));
        }
      } finally {
        setIsSendingOtp(false);
      }
    },
    [otpState, sendOTP, t],
  );

  const handleClearSmsError = useCallback(() => {
    setOtpState(prev => {
      if (!prev || prev.mode !== 'sms') return prev;
      return { ...prev, mode: 'sms' as const, smsError: false };
    });
  }, []);

  const handleOtpBack = useCallback(() => {
    setOtpState(null);
    navigate('/register/phone', { replace: true });
  }, [navigate]);

  const clearOtpState = useCallback(() => setOtpState(null), []);

  return {
    otpState,
    isSendingOtp,
    startOtpFlow,
    handleOtpVerificationSuccess,
    handleOtpResend,
    handleEmailFallback,
    handleSmsFallback,
    handleClearSmsError,
    handleOtpBack,
    clearOtpState,
  };
};
